import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime } from 'rxjs';

import { formatCompactOrIsoDate } from '../../shared/trade-formatters';
import { InvoicesService } from '../invoices.service';
import {
  InvoiceRelatedRecord,
  RelatedRecordLinkTypeCode,
  RelatedRecordSelection,
} from '../invoices.types';

@Component({
  selector: 'app-related-records-selector',
  imports: [CommonModule, FormsModule],
  templateUrl: './related-records-selector.html',
  styleUrl: './related-records-selector.css',
})
export class RelatedRecordsSelector implements OnInit, OnChanges, OnDestroy {
  private readonly invoicesService = inject(InvoicesService);
  private readonly searchChanges = new Subject<void>();
  private searchSubscription?: Subscription;
  private requestId = 0;

  @Input({ required: true }) linkTypeCode!: RelatedRecordLinkTypeCode;
  @Input() bondDocumentationId: number | null = null;
  @Input() initialSelections: RelatedRecordSelection[] = [];

  @Output() readonly cancelled = new EventEmitter<void>();
  @Output() readonly saved = new EventEmitter<RelatedRecordSelection[]>();

  protected readonly items = signal<InvoiceRelatedRecord[]>([]);
  protected readonly searchTerm = signal('');
  protected readonly seriesNumber = signal('');
  protected readonly pageNumber = signal(1);
  protected readonly pageSize = signal(25);
  protected readonly totalRecords = signal(0);
  protected readonly totalPages = signal(0);
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly selections = signal<Map<string, RelatedRecordSelection>>(new Map());

  protected get isSecurityMode(): boolean {
    return this.linkTypeCode === 'SECURITY';
  }

  protected get modeLabel(): string {
    switch (this.linkTypeCode) {
      case 'DEAL':
        return 'Deal ID';
      case 'SECURITY':
        return 'Security ID';
      default:
        return 'Program';
    }
  }

  protected get visibleColumnCount(): number {
    return this.isSecurityMode ? 8 : 13;
  }

  ngOnInit(): void {
    this.initializeSelections();
    this.searchSubscription = this.searchChanges.pipe(
      debounceTime(300),
    ).subscribe(() => {
      this.pageNumber.set(1);
      this.loadPage();
    });
    this.loadPage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialSelections'] && !changes['initialSelections'].firstChange) {
      this.initializeSelections();
    }
    if (
      (changes['linkTypeCode'] && !changes['linkTypeCode'].firstChange)
      || (changes['bondDocumentationId'] && !changes['bondDocumentationId'].firstChange)
    ) {
      this.searchTerm.set('');
      this.seriesNumber.set('');
      this.pageNumber.set(1);
      this.initializeSelections();
      this.loadPage();
    }
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  protected onSearchChange(value: string): void {
    this.searchTerm.set(value);
    this.searchChanges.next();
  }

  protected onSeriesNumberChange(value: string): void {
    this.seriesNumber.set(value);
    this.searchChanges.next();
  }

  protected goToPage(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.pageNumber()) {
      return;
    }

    this.pageNumber.set(page);
    this.loadPage();
  }

  protected isSelected(tradeId: string): boolean {
    return this.selections().has(tradeId);
  }

  protected toggleSelection(record: InvoiceRelatedRecord, checked: boolean): void {
    const next = new Map(this.selections());
    if (checked) {
      next.set(record.tradeId, next.get(record.tradeId) ?? { record, amountText: '' });
    } else {
      next.delete(record.tradeId);
    }
    this.selections.set(next);
  }

  protected amountFor(tradeId: string): string {
    return this.selections().get(tradeId)?.amountText ?? '';
  }

  protected updateAmount(record: InvoiceRelatedRecord, value: string | number | null): void {
    const amountText = value === null ? '' : String(value);
    const next = new Map(this.selections());
    next.set(record.tradeId, { record, amountText });
    this.selections.set(next);
  }

  protected save(): void {
    const selections = [...this.selections().values()];
    const invalid = selections.some(({ amountText }) => {
      if (amountText.trim() === '') {
        return true;
      }
      const amount = Number(amountText);
      return !Number.isFinite(amount) || amount < 0;
    });
    if (invalid) {
      this.errorMessage.set('Each selected record needs a valid non-negative amount.');
      return;
    }

    this.saved.emit(selections);
  }

  protected formatDate(value: string): string {
    return value ? formatCompactOrIsoDate(value) : '-';
  }

  private initializeSelections(): void {
    this.selections.set(new Map(this.initialSelections.map((selection) => [selection.record.tradeId, selection])));
  }

  private loadPage(): void {
    if (this.linkTypeCode === 'BOND_DOCUMENTATION' && !this.bondDocumentationId) {
      this.items.set([]);
      this.totalRecords.set(0);
      this.totalPages.set(0);
      this.errorMessage.set('Select a program before loading related records.');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');
    const requestId = ++this.requestId;
    this.invoicesService.getInvoiceRelatedRecords({
      linkTypeCode: this.linkTypeCode,
      search: this.searchTerm(),
      seriesNumber: this.seriesNumber(),
      bondDocumentationId: this.bondDocumentationId,
      pageNumber: this.pageNumber(),
      pageSize: this.pageSize(),
    }).subscribe({
      next: (page) => {
        if (requestId !== this.requestId) {
          return;
        }
        this.items.set(page.items);
        this.pageNumber.set(page.pageNumber);
        this.pageSize.set(page.pageSize);
        this.totalRecords.set(page.totalRecords);
        this.totalPages.set(page.totalPages);
        this.isLoading.set(false);
      },
      error: () => {
        if (requestId !== this.requestId) {
          return;
        }
        this.items.set([]);
        this.totalRecords.set(0);
        this.totalPages.set(0);
        this.errorMessage.set('Unable to load related records.');
        this.isLoading.set(false);
      },
    });
  }
}
